import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { eventsApi } from "../api/eventsApi";
import { formatApiError } from "../../util/formatApiError";
import { showToast, showSuccessToast } from "../../util/appToast";
import { voiceConsentApi, VOICE_CONSENT_SOURCE } from "../api/voiceConsentApi";
import { EVENT_TERMS_VERSION } from "../content/eventsTermsContent";
import { EventTermsBookingBlock, EventTermsModal } from "../components/EventTermsAndConditions";
import EventTermsDisclaimerStrip from "../components/EventTermsDisclaimerStrip";
import EventBannerCarousel from "../components/EventBannerCarousel";
import { GstPaymentSummary } from "../components/GstPaymentSummary";
import { formatCommitteeLabel, committeeOptionsFromEvent } from "../utils/committeeLabels";
import { dualEntryFee, filterItemsByQuery, packageSearchText, selectedItemsFirst, winningPrizeText } from "../utils/packageList";
import { InvoiceWhatsAppActions } from "../components/InvoiceWhatsAppActions";
import CommitteeStatsBlock from "../components/CommitteeStatsBlock";
import CommitteeSelect from "../components/CommitteeSelect";
import "../events.css";

const STEPS = [
  { id: 1, label: "Package", icon: "P" },
  { id: 2, label: "Details", icon: "D" },
  { id: 3, label: "Verify", icon: "V" },
  { id: 4, label: "Pay", icon: "$" },
  { id: 5, label: "Booking", icon: "✓" }
];

const SERVICE_LABELS = {
  VALET: "Valet Pickup / Drop",
  SHARED_CART_ONE_WAY: "Shared Cart One Way",
  SHARED_CART_ROUND_TRIP: "Shared Cart Round Trip",
  PERSONAL_CART_ONE_WAY: "Personal Cart One Way",
  PERSONAL_CART_ROUND_TRIP: "Personal Cart Round Trip",
  PARKING: "Parking"
};

const fmtService = s => SERVICE_LABELS[s] || s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

/** Only these add-on rows are shown for Platinum / Elite (no other optional add-ons). */
const PLATINUM_ADDON_CODES = ["SHARED_CART_ROUND_TRIP"];
const ELITE_ADDON_CODES = ["PERSONAL_CART_ROUND_TRIP", "VALET"];

/** Pre-selected addons for premium packages (matches backend included_services; priced as ₹0 extra). */
function getDefaultAddonsForPackage(packageCode) {
  if (packageCode === "PLATINUM") {
    return [{ service_code: "SHARED_CART_ROUND_TRIP", quantity: 2 }];
  }
  if (packageCode === "ELITE") {
    return [
      { service_code: "PERSONAL_CART_ROUND_TRIP", quantity: 1 },
      { service_code: "VALET", quantity: 1 }
    ];
  }
  if (packageCode === "GOLD") {
    return [];
  }
  return [];
}

/* ── Voice / IVR support callback consent ──────────────────────────
 * This exact text is snapshotted on the server with every opt-in so
 * MSG91 can be shown sample records on demand (see T&C clause 53).
 * Edit carefully; any change should be paired with a T&C version bump.
 */
const VOICE_CONSENT_TEXT =
  "I authorise ParkAdda and its authorised voice service providers (including MSG91) to place transactional voice calls and interactive voice response (IVR) support callbacks on my registered mobile number for booking assistance, on-ground support coordination, incident handling, and refund/settlement queries. This consent is transactional only (no marketing), is valid for up to 6 months, and I may withdraw it at any time from my booking details page or by writing to support@parkadda.com.";

const parseApiError = formatApiError;

const EventBookingPlaceholder = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const eventId = searchParams.get("event_id") || "";
  const packageCodeFromUrl = useRef((searchParams.get("package_code") || "").trim());
  /* Per-committee registration links: ?committee=CSI pins the committee field so a
     chapter can hand out one URL and every entry lands under them. */
  const committeeFromUrl = useRef(
    (searchParams.get("committee") || searchParams.get("committee_id") || "").trim()
  );
  const prebookMode = searchParams.get("prebook") === "1" || searchParams.get("pay") === "1";
  const bookingIdFromUrl = useRef((searchParams.get("booking_id") || "").trim());
  const prebookHydrated = useRef(false);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState("");

  const [bookingId, setBookingId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [bookingResult, setBookingResult] = useState(null);
  const bookedCartRef = useRef("");
  const bookedDetailsRef = useRef("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const termsStorageKey = bookingId ? "pa_ev_terms_" + bookingId : "";
  const setTermsAcceptedSafe = useCallback(
    function (v) {
      setTermsAccepted(v);
      if (v && termsStorageKey) {
        try {
          sessionStorage.setItem(termsStorageKey, EVENT_TERMS_VERSION);
        } catch (e) {}
      }
    },
    [termsStorageKey]
  );

  useEffect(
    function () {
      if (!termsStorageKey) return;
      try {
        if (sessionStorage.getItem(termsStorageKey) === EVENT_TERMS_VERSION) setTermsAccepted(true);
      } catch (e) {}
    },
    [termsStorageKey]
  );

  const [form, setForm] = useState({
    package_code: "",
    addons: [],
    name: "",
    email: "",
    phone: "",
    vehicle_type: "4W",
    vehicle_number: "",
    vehicle_brand: "",
    vehicle_color: "",
    registration_committee: "",
    college_name: "",
    year_of_study: "",
    course: ""
  });
  const [cartCodes, setCartCodes] = useState([]);
  const [pkgQuery, setPkgQuery] = useState("");

  /* Voice / IVR support-callback consent (MSG91 compliance).
   * Unchecked by default — MSG91 requires explicit opt-in, so the user
   * must actively tick this.  The booking itself is *not* blocked if
   * the user declines; we just record the decision either way. */
  const [voiceConsent, setVoiceConsent] = useState(false);

  const packages = useMemo(() => (event && Array.isArray(event.packages) ? event.packages : []), [event]);
  const selectedPkg = useMemo(() => {
    var code = form.package_code || (cartCodes.length ? cartCodes[cartCodes.length - 1] : "");
    return packages.find(p => p.package_code === code);
  }, [packages, form.package_code, cartCodes]);
  const cartPackages = useMemo(function () {
    return cartCodes.map(function (code) { return packages.find(function (p) { return p.package_code === code; }); }).filter(Boolean);
  }, [cartCodes, packages]);
  const selectedCodes = useMemo(function () {
    if (cartCodes.length) return cartCodes;
    return form.package_code ? [form.package_code] : [];
  }, [cartCodes, form.package_code]);
  const visiblePackages = useMemo(function () {
    var filtered = filterItemsByQuery(packages, pkgQuery, packageSearchText);
    return selectedItemsFirst(filtered, selectedCodes, function (pkg) { return pkg.package_code; });
  }, [packages, pkgQuery, selectedCodes]);
  const selectedPinnedPackages = useMemo(function () {
    var allow = {};
    selectedCodes.forEach(function (code) { if (code) allow[code] = true; });
    return selectedItemsFirst(
      packages.filter(function (pkg) { return allow[pkg.package_code]; }),
      selectedCodes,
      function (pkg) { return pkg.package_code; }
    );
  }, [packages, selectedCodes]);
  const remainingVisiblePackages = useMemo(function () {
    var allow = {};
    selectedCodes.forEach(function (code) { if (code) allow[code] = true; });
    return filterItemsByQuery(packages, pkgQuery, packageSearchText).filter(function (pkg) {
      return !allow[pkg.package_code];
    });
  }, [packages, pkgQuery, selectedCodes]);
  const scrollToCart = useCallback(function () {
    setTimeout(function () {
      var el = document.getElementById("bk-cart");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);
  }, []);
  const toggleCart = useCallback(function (code) {
    setCartCodes(function (prev) {
      var next = prev.indexOf(code) >= 0 ? prev.filter(function (c) { return c !== code; }) : prev.concat([code]);
      setForm(function (p) {
        return Object.assign({}, p, { package_code: next.length ? next[next.length - 1] : "" });
      });
      return next;
    });
  }, []);
  const clearCart = useCallback(function () {
    setCartCodes([]);
    setForm(function (p) {
      return Object.assign({}, p, { package_code: "" });
    });
    setQuote(null);
  }, []);
  const cartLineItems = useMemo(function () {
    if (quote && Array.isArray(quote.lines) && quote.lines.length) return quote.lines;
    return cartPackages.map(function (pkg) {
      return {
        package_code: pkg.package_code,
        package_name: pkg.name,
        price: pkg.price,
        total_amount: pkg.price
      };
    });
  }, [quote, cartPackages]);
  const isCompetitionPkg = useCallback(function (pkg) {
    return !!(pkg && (pkg.kind === "COMPETITION" || (pkg.metadata && pkg.metadata.committee)));
  }, []);
  const requiresVehicle = useMemo(function () {
    return selectedPkg ? !isCompetitionPkg(selectedPkg) : false;
  }, [selectedPkg, isCompetitionPkg]);
  const packagesByCommittee = useMemo(function () {
    var groups = {};
    packages.forEach(function (pkg) {
      var c = pkg.committee || (pkg.metadata && pkg.metadata.committee) || "Other";
      if (!groups[c]) groups[c] = [];
      groups[c].push(pkg);
    });
    return groups;
  }, [packages]);
  const committeeOptions = useMemo(function () {
    return committeeOptionsFromEvent(event, packagesByCommittee);
  }, [event, packagesByCommittee]);
  /* Resolve ?committee= to a real option code (case-insensitive). An unknown code
     resolves to "" so the field stays a normal dropdown rather than locking the
     user onto a committee that does not exist for this event. */
  const lockedCommittee = useMemo(function () {
    var raw = committeeFromUrl.current;
    if (!raw || !committeeOptions.length) return "";
    var want = raw.toUpperCase();
    var hit = committeeOptions.find(function (opt) {
      return String(opt.code || "").toUpperCase() === want;
    });
    return hit ? hit.code : "";
  }, [committeeOptions]);

  useEffect(function () {
    if (!lockedCommittee) return;
    setForm(function (prev) {
      if (prev.registration_committee === lockedCommittee) return prev;
      return Object.assign({}, prev, { registration_committee: lockedCommittee });
    });
  }, [lockedCommittee]);
  const isCompetitionEvent = useMemo(function () {
    return packages.length > 0 && packages.every(isCompetitionPkg);
  }, [packages, isCompetitionPkg]);
  const cartSignature = useMemo(function () {
    return JSON.stringify({
      codes: cartCodes,
      package_code: form.package_code,
      addons: form.addons
    });
  }, [cartCodes, form.package_code, form.addons]);

  useEffect(function () {
    if (!bookingId || prebookMode) return;
    if (!bookedCartRef.current) return;
    if (cartSignature !== bookedCartRef.current) {
      setBookingId("");
      setOtpSent(false);
      setOtpVerified(false);
      setOtp("");
      bookedCartRef.current = "";
    }
  }, [bookingId, cartSignature, prebookMode]);
  const allAddons = useMemo(() => (event && Array.isArray(event.addons) ? event.addons : []), [event]);
  const addons = useMemo(() => {
    var vt = selectedPkg ? selectedPkg.vehicle_type : "";
    var base = allAddons.filter(function (svc) {
      var types = svc.applicable_vehicle_types;
      if (!types || !Array.isArray(types) || types.length === 0) return true;
      return types.includes(vt);
    });
    var pk = selectedPkg && selectedPkg.package_code;
    if (pk === "PLATINUM") {
      var ps = new Set(PLATINUM_ADDON_CODES);
      return base.filter(function (svc) { return ps.has(svc.service_code); });
    }
    if (pk === "ELITE") {
      var es = new Set(ELITE_ADDON_CODES);
      var list = base.filter(function (svc) { return es.has(svc.service_code); });
      var order = { PERSONAL_CART_ROUND_TRIP: 0, VALET: 1 };
      return list.slice().sort(function (a, b) {
        var oa = order[a.service_code];
        var ob = order[b.service_code];
        return (oa != null ? oa : 9) - (ob != null ? ob : 9);
      });
    }
    return base;
  }, [allAddons, selectedPkg]);

  const lockedAddonTier = form.package_code === "PLATINUM" || form.package_code === "ELITE";

  const clearMsg = () => { setError(""); setSuccess(""); };
  const notifyError = function (msg) {
    var text = formatApiError(msg);
    setError(text);
    showToast(text, "error");
  };
  const notifySuccess = function (msg) {
    setSuccess(msg);
    showSuccessToast(msg);
  };
  const field = (name, value) => {
    var v = typeof value === "function" ? value : function () { return value; };
    setForm(function (prev) {
      var next = Object.assign({}, prev, { [name]: v(prev[name]) });
      if (name === "package_code") {
        var pkg = packages.find(function (p) { return p.package_code === next.package_code; });
        next.vehicle_type = pkg && pkg.vehicle_type ? pkg.vehicle_type : "4W";
        if (prev.package_code !== next.package_code) {
          next.addons = getDefaultAddonsForPackage(next.package_code);
        }
      }
      return next;
    });
    setFieldErrors(function (prev) { return Object.assign({}, prev, { [name]: "" }); });
    clearMsg();
  };
  const goStep = useCallback(n => {
    setStep(n);
    setHighestStep(prev => Math.max(prev, n));
    clearMsg();
  }, []);

  useEffect(() => { document.title = isCompetitionEvent ? "Book Competition Entry | ParkAdda" : "Book Event Parking | ParkAdda"; }, [isCompetitionEvent]);
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { document.body.removeChild(s); };
  }, []);

  useEffect(() => {
    if (!eventId) { setLoading(false); notifyError("This booking link is missing an event. Open the event page and tap Book again."); return; }
    let live = true;
    (async () => {
      try {
        const data = await eventsApi.getEventDetail(eventId);
        if (!live) return;
        setEvent(data);
      } catch (e) { if (live) setError(e.message || "Unable to load event"); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [eventId]);

  /** Pre-select package (and default addons) when opening booking from event view with ?package_code= */
  useEffect(() => {
    if (prebookMode) return;
    if (!event || !packageCodeFromUrl.current) return;
    var code = packageCodeFromUrl.current;
    var pkgs = event.packages && Array.isArray(event.packages) ? event.packages : [];
    var pkg = pkgs.find(function (p) { return p.package_code === code; });
    var vehicleType = pkg && pkg.vehicle_type;
    if (!pkg && Array.isArray(event.pricing_chart)) {
      var row = event.pricing_chart.find(function (r) { return r.package_code === code; });
      if (!row) return;
      vehicleType = row.vehicle_type;
    } else if (!pkg) {
      return;
    }
    packageCodeFromUrl.current = "";
    setCartCodes([code]);
    setForm(function (prev) {
      return Object.assign({}, prev, {
        package_code: code,
        vehicle_type: vehicleType || prev.vehicle_type,
        addons: getDefaultAddonsForPackage(code)
      });
    });
  }, [event, prebookMode]);

  /** Admin prebook: open pay step with existing booking (OTP skipped). */
  useEffect(() => {
    if (!prebookMode || !bookingIdFromUrl.current || !event) return;
    if (prebookHydrated.current) return;
    var bid = bookingIdFromUrl.current;
    let cancelled = false;
    (async () => {
      try {
        const payload = await eventsApi.lookupBooking(bid);
        if (cancelled) return;
        prebookHydrated.current = true;
        setBookingId(bid);
        setOtpVerified(true);
        setOtpSent(true);
        var pk = payload.package;
        var addonRows = payload.selected_addons || [];
        var addonsForm = addonRows.map(function (a) {
          return { service_code: a.service_code, quantity: a.quantity != null ? a.quantity : 1 };
        });
        var orderLines = (payload.order && Array.isArray(payload.order.lines)) ? payload.order.lines : [];
        var hydratedCodes = orderLines.map(function (line) { return line.package_code; }).filter(Boolean);
        if (!hydratedCodes.length && pk && pk.package_code) hydratedCodes = [pk.package_code];
        setCartCodes(hydratedCodes);
        setForm(function (prev) {
          return Object.assign({}, prev, {
            package_code: (hydratedCodes.length ? hydratedCodes[hydratedCodes.length - 1] : (pk && pk.package_code)) || prev.package_code,
            name: payload.name || prev.name,
            email: payload.email || prev.email,
            phone: payload.phone || prev.phone,
            vehicle_type: payload.vehicle_type || prev.vehicle_type,
            vehicle_number: payload.vehicle_number || prev.vehicle_number,
            vehicle_brand: payload.vehicle_brand || "",
            vehicle_color: payload.vehicle_color || "",
            addons: addonsForm.length ? addonsForm : getDefaultAddonsForPackage((pk && pk.package_code) || "")
          });
        });
        setQuote({
          subtotal_amount: payload.subtotal_amount,
          gst_percent: payload.gst_percent,
          gst_amount: payload.gst_amount,
          total_amount: payload.total_amount,
          lines: orderLines.map(function (line) {
            return {
              package_code: line.package_code,
              package_name: line.game_name || line.package_name,
              price: line.amount,
              total_amount: line.amount
            };
          })
        });
        if (payload.booking_status === "CONFIRMED" || payload.payment_status === "SUCCESS") {
          setBookingResult(payload);
          setStep(5);
          setHighestStep(5);
        } else {
          setStep(4);
          setHighestStep(4);
        }
      } catch (e) {
        if (!cancelled) setError(parseApiError(e.message) || "Could not load booking");
      }
    })();
    return () => { cancelled = true; };
  }, [event, prebookMode, eventId]);

  useEffect(() => {
    if (!form.addons.length) return;
    var validCodes = addons.map(function (a) { return a.service_code; });
    var filtered = form.addons.filter(function (a) { return validCodes.includes(a.service_code); });
    if (filtered.length !== form.addons.length)
      setForm(prev => ({ ...prev, addons: filtered }));
  }, [addons, form.addons]);

  useEffect(() => {
    if (!eventId || !(form.package_code || cartCodes.length)) {
      setQuote(null);
      setQuoteError("");
      return;
    }
    const t = setTimeout(async () => {
      try {
        setBusy("quote");
        const res = await eventsApi.quote({
          event_id: eventId,
          package_code: form.package_code,
          package_codes: isCompetitionEvent && cartCodes.length ? cartCodes : undefined,
          addons: form.addons
        }, { silent: true });
        setQuote(res);
        setQuoteError("");
      } catch (e) {
        setQuote(null);
        setQuoteError(parseApiError(e.message) || "Could not refresh the price. Try again.");
      } finally { setBusy(""); }
    }, 200);
    return () => clearTimeout(t);
  }, [eventId, form.package_code, JSON.stringify(form.addons), JSON.stringify(cartCodes), isCompetitionEvent]);

  const validateStep2 = () => {
    const e = {};
    if (!(form.name && form.name.trim())) e.name = "Required";
    if (!(form.email && form.email.trim())) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Invalid email";
    if (!(form.phone && form.phone.trim())) e.phone = "Required";
    else if (!/^\d{10}$/.test(form.phone.trim())) e.phone = "Must be 10 digits";
    if (requiresVehicle && !(form.vehicle_number && form.vehicle_number.trim())) e.vehicle_number = "Required";
    if (isCompetitionEvent && !form.registration_committee) e.registration_committee = "Please select a committee";
    if (isCompetitionEvent && !(form.college_name && form.college_name.trim())) e.college_name = "College Name is required";
    return e;
  };

  const handleProceedStep1 = async () => {
    if (!form.package_code && !cartCodes.length) {
      setFieldErrors({ package_code: "Select at least one game" });
      notifyError("Please add at least one game to the cart.");
      return;
    }
    clearMsg();
    try {
      setBusy("quote");
      const res = await eventsApi.quote({
        event_id: eventId,
        package_code: form.package_code,
        package_codes: isCompetitionEvent && cartCodes.length ? cartCodes : undefined,
        addons: form.addons
      });
      setQuote(res);
      setQuoteError("");
      goStep(2);
    } catch (e) {
      setQuoteError(parseApiError(e.message) || "Could not price this cart.");
      setError(parseApiError(e.message));
    } finally { setBusy(""); }
  };

  const handleProceedStep2 = async () => {
    const errs = validateStep2();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      notifyError("Please fix the highlighted fields.");
      return;
    }
    setFieldErrors({});
    clearMsg();
    var detailsSig = JSON.stringify({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      registration_committee: form.registration_committee || "",
      college_name: (form.college_name || "").trim(),
      year_of_study: (form.year_of_study || "").trim(),
      course: (form.course || "").trim()
    });
    if (bookingId && bookedCartRef.current === cartSignature && bookedDetailsRef.current === detailsSig) {
      goStep(3);
      return;
    }

    try {
      setBusy("create");
      const res = await eventsApi.createBooking({
        event_id: eventId,
        package_code: form.package_code,
        package_codes: isCompetitionEvent && cartCodes.length ? cartCodes : undefined,
        addons: form.addons,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        vehicle_type: form.vehicle_type,
        vehicle_number: requiresVehicle
          ? form.vehicle_number.toUpperCase().replace(/[^A-Z0-9]/g, "")
          : "",
        vehicle_brand: form.vehicle_brand,
        vehicle_color: form.vehicle_color,
        registration_committee: isCompetitionEvent ? (form.registration_committee || "") : "",
        college_name: isCompetitionEvent ? (form.college_name || "").trim() : "",
        year_of_study: isCompetitionEvent ? (form.year_of_study || "").trim() : "",
        course: isCompetitionEvent ? (form.course || "").trim() : ""
      });
      setBookingId(res.booking_id);

      /* Record the voice / IVR consent decision against this booking.
       * Fire-and-forget: we do not want a compliance-store hiccup to
       * block the booking flow, but we DO surface a warning so support
       * can follow up manually if the store is unreachable. */
      voiceConsentApi
        .record({
          phone: form.phone.trim(),
          consent_given: !!voiceConsent,
          source: VOICE_CONSENT_SOURCE.WEBSITE_BOOKING,
          name: form.name.trim(),
          email: form.email.trim(),
          consent_text_version: EVENT_TERMS_VERSION,
          consent_text_snapshot: VOICE_CONSENT_TEXT,
          consent_url:
            typeof window !== "undefined" && window.location
              ? window.location.href
              : "",
          booking_id: res.booking_id,
          event_id: eventId,
        })
        .catch((err) => {
          if (typeof console !== "undefined" && console.warn) {
            console.warn("voice-consent record failed:", err && err.message);
          }
        });

      bookedCartRef.current = cartSignature;
      bookedDetailsRef.current = detailsSig;
      try {
        await eventsApi.sendOtp({ booking_id: res.booking_id });
        setOtpSent(true);
        notifySuccess("OTP sent to your phone.");
      } catch (otpErr) {
        setOtpSent(false);
        setError(parseApiError(otpErr.message) || "Booking created. Tap Send OTP to verify your phone.");
      }
      goStep(3);
    } catch (e) {
      setError(parseApiError(e.message));
    } finally { setBusy(""); }
  };

  const handleSendOtp = async () => {
    if (!bookingId) return;
    clearMsg();
    try {
      setBusy("otp");
      await eventsApi.sendOtp({ booking_id: bookingId });
      setOtpSent(true);
      notifySuccess("OTP sent to your phone.");
    } catch (e) { setError(parseApiError(e.message)); }
    finally { setBusy(""); }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setFieldErrors(p => ({ ...p, otp: "Enter 4-digit OTP" }));
      return;
    }
    clearMsg();
    try {
      setBusy("verify");
      await eventsApi.verifyOtp({ booking_id: bookingId, otp: otp.trim() });
      setOtpVerified(true);
      setFieldErrors(p => ({ ...p, otp: "" }));
      notifySuccess("Phone verified successfully.");
    } catch (e) {
      const msg = parseApiError(e.message) || "Invalid OTP. Please try again.";
      setError(msg);
      setFieldErrors(p => ({ ...p, otp: msg }));
      setOtp("");
    }
    finally { setBusy(""); }
  };

  const handleProceedStep3 = async () => {
    clearMsg();
    if (!otpSent) { await handleSendOtp(); return; }
    if (!otpVerified) { await handleVerifyOtp(); return; }
    goStep(4);
  };

  const pollBookingResult = useCallback(async (bid, attempt) => {
    if (attempt > 15) return;
    try {
      var res = await eventsApi.lookupBooking(bid);
      setBookingResult(res);
      if (res.payment_status === "SUCCESS" || res.booking_status === "CONFIRMED") return;
      if (res.payment_status === "FAILED" || res.booking_status === "FAILED") return;
      if (res.booking_status === "CANCELLED") return;
      setTimeout(function () { pollBookingResult(bid, attempt + 1); }, 3000);
    } catch (e) {
      setTimeout(function () { pollBookingResult(bid, attempt + 1); }, 3000);
    }
  }, []);

  const handlePay = async () => {
    clearMsg();
    try {
      setBusy("pay");
      const data = await eventsApi.initPayment({
        booking_id: bookingId,
        terms_accepted: true,
        terms_version: EVENT_TERMS_VERSION
      });
      if (window.Cashfree) {
        const mode = process.env.REACT_APP_CASHFREE_ENV === "sandbox" ? "sandbox" : "production";
        var cf = window.Cashfree({ mode });
        cf.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_modal",
          appearance: { width: "425px", height: "700px" }
        }).then(function (result) {
          setBusy("");
          goStep(5);
          pollBookingResult(bookingId, 0);
        }).catch(function () {
          setBusy("");
          setError("Payment window closed or failed. You can try again.");
        });
      } else {
        goStep(5);
        pollBookingResult(bookingId, 0);
      }
    } catch (e) { setError(parseApiError(e.message)); setBusy(""); }
  };

  const gstLabel = event && event.gst_inclusive ? "*incl. GST" : "*excl. GST";

  const renderCompCard = function (pkg) {
    var active = cartCodes.indexOf(pkg.package_code) >= 0 || form.package_code === pkg.package_code;
    var meta = pkg.metadata || {};
    var imgUrl = pkg.image_url || meta.image_url || "";
    var dualFee = dualEntryFee(pkg);
    return (
      <button
        key={pkg.package_code}
        type="button"
        className={"bk-pkg-card bk-pkg-card--competition" + (active ? " bk-pkg-card--active" : "") + (fieldErrors.package_code ? " bk-pkg-card--err" : "")}
        aria-pressed={active}
        onClick={function () {
          toggleCart(pkg.package_code);
          scrollToCart();
        }}
      >
        {imgUrl ? (
          <span className="bk-pkg-card__media">
            <img src={imgUrl} alt={pkg.image_alt || meta.image_alt || pkg.name} loading="lazy" />
          </span>
        ) : null}
        <span className={"bk-pkg-card__price" + (dualFee ? " bk-pkg-card__price--dual" : "")}>
          <span className="bk-pkg-card__price-kicker">{dualFee ? "ENTRY FEE" : "Entry"}</span>
          {dualFee ? dualFee : ("\u20B9" + parseFloat(pkg.price).toFixed(0))}
          <span className="ev-gst-note">{gstLabel}</span>
        </span>
        {winningPrizeText(meta.prize_pool) ? (
          <span className="bk-pkg-card__win">{winningPrizeText(meta.prize_pool)}</span>
        ) : null}
        <span className="bk-pkg-card__name">{pkg.name}</span>
        {(pkg.description || meta.category) ? (
          <span className="bk-pkg-card__desc">{pkg.description || meta.category}</span>
        ) : null}
        <span className="bk-pkg-card__inc-list">
          {meta.team_size ? <span className="bk-pkg-card__inc-chip">{meta.team_size}</span> : null}
        </span>
        {active && <span className="bk-pkg-card__check">&#10003;</span>}
      </button>
    );
  };

  const fmtDate = v => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? "—" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return (
    <div className="bk-page">
      <div className="bk-loader-wrap"><div className="bk-spinner" /><p>Loading event details...</p></div>
    </div>
  );

  if (!event) return (
    <div className="bk-page">
      <div className="bk-container">
        <div className="bk-error-full">
          <h2>Event not found</h2>
          <p>{error || "The event you're looking for doesn't exist."}</p>
          <Link to="/events" className="bk-btn bk-btn--primary">Browse Events</Link>
        </div>
      </div>
    </div>
  );

  if (event.is_booking_open === false) return (
    <div className="bk-page">
      <div className="bk-container">
        <div className="bk-error-full">
          <div className="bk-closed-icon">{"\u26D4"}</div>
          <h2>{event.booking_status_label || "Booking Closed"}</h2>
          <p>Booking for <strong>{event.title}</strong> is not available right now.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
            <Link to={"/events/view?event_id=" + eventId} className="bk-btn bk-btn--ghost">View Event Details</Link>
            <Link to="/events/booking" className="bk-btn bk-btn--ghost">Find a booking</Link>
            <Link to="/events" className="bk-btn bk-btn--primary">Browse Events</Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bk-page">
      <div className="bk-container">
        {/* Header */}
        <div className="bk-header">
          <h1 className="bk-page-title">Checkout</h1>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
            <Link to="/events" className="bk-back-link">All events</Link>
            <Link to="/events/booking" className="bk-back-link">Find a booking</Link>
          </div>
        </div>

        {/* Event strip */}
        <div className="bk-event-strip">
          <div className="bk-event-strip__banner">
            <EventBannerCarousel event={event} size="strip" />
          </div>
          <div className="bk-event-strip__info">
            <h3>{event.title}</h3>
            <a
              href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent((event.venue_name || "") + ", " + (event.address || "") + ", " + (event.city || ""))}
              target="_blank"
              rel="noopener noreferrer"
              className="bk-event-strip__venue-link"
            >{"\uD83D\uDCCD"} {event.venue_name}, {event.city}</a>
            <p className="bk-event-strip__date">{fmtDate(event.start_datetime)}</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="bk-stepper">
          {STEPS.map((s, i) => {
            const done = highestStep > s.id || (s.id === 3 && otpVerified) || (s.id === 2 && bookingId);
            const active = step === s.id;
            return (
              <React.Fragment key={s.id}>
                {i > 0 && <div className={`bk-stepper__line ${done ? "done" : ""}`} />}
                <button
                  type="button"
                  className={`bk-stepper__dot ${active ? "active" : ""} ${done ? "done" : ""}`}
                  onClick={() => step < 5 && s.id <= highestStep && goStep(s.id)}
                  disabled={s.id > highestStep || step >= 5}
                >
                  <span className="bk-stepper__num">{done ? "✓" : s.id}</span>
                  <span className="bk-stepper__label">{s.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Alerts */}
        {error && <div className="bk-alert bk-alert--error">{error}</div>}
        {success && <div className="bk-alert bk-alert--success">{success}</div>}

        {/* Main grid */}
        <div className={"bk-grid" + (step === 1 ? " bk-grid--catalog" : "")}>
          <div className="bk-main">
            {/* STEP 1 */}
            {step === 1 && (
              <div className="bk-card bk-card--step">
                <div className="bk-card__head">
                  <span className="bk-card__step-num">1</span>
                  <div>
                    <h2 className="bk-card__title">{isCompetitionEvent ? "Select Event / Competition" : "Select Parking Package"}</h2>
                    <p className="bk-card__sub">{isCompetitionEvent
                      ? (
                        <>
                          Add one or more competitions or workshops. Entry is collected online; each game gets its own QR after payment.
                          <span className="bk-card__note">* Tap a card to add or remove it. Selected games stay pinned at the top.</span>
                        </>
                      )
                      : "Choose from available parking tiers — each includes secured bay access with QR-validated entry. Premium tiers include cart mobility and valet services."}</p>
                  </div>
                </div>

                {isCompetitionEvent ? (
                  <>
                    {cartPackages.length > 0 ? (
                      <div className="bk-cart" id="bk-cart">
                        <div className="bk-cart__head">
                          <div>
                            <h3 className="bk-cart__title">Your cart</h3>
                            <p className="bk-cart__sub">{cartPackages.length} game{cartPackages.length === 1 ? "" : "s"} selected · tap a card to add or remove</p>
                          </div>
                          <button type="button" className="bk-cart__clear" onClick={clearCart}>Clear all</button>
                        </div>
                        <ul className="bk-cart__list">
                          {cartPackages.map(function (pkg) {
                            var meta = pkg.metadata || {};
                            var imgUrl = pkg.image_url || meta.image_url || "";
                            var committee = formatCommitteeLabel(pkg.committee || meta.committee || "");
                            return (
                              <li className="bk-cart__row" key={pkg.package_code}>
                                {imgUrl ? (
                                  <img className="bk-cart__thumb" src={imgUrl} alt="" />
                                ) : (
                                  <span className="bk-cart__thumb bk-cart__thumb--fallback" aria-hidden="true">{(pkg.name || "?").charAt(0)}</span>
                                )}
                                <div className="bk-cart__meta">
                                  <strong>{pkg.name}</strong>
                                  <span>{[committee, meta.team_size].filter(Boolean).join(" · ") || "Competition entry"}</span>
                                </div>
                                <span className="bk-cart__price">&#8377;{parseFloat(pkg.price).toFixed(0)}</span>
                                <button type="button" className="bk-cart__remove" aria-label={"Remove " + pkg.name} onClick={function () { toggleCart(pkg.package_code); }}>×</button>
                              </li>
                            );
                          })}
                        </ul>
                        <div className="bk-cart__foot">
                          <span>Cart total{event.gst_inclusive ? " (incl. GST)" : ""}</span>
                          <strong>&#8377;{quote && quote.total_amount ? parseFloat(quote.total_amount).toFixed(0) : cartPackages.reduce(function (sum, pkg) { return sum + (parseFloat(pkg.price) || 0); }, 0).toFixed(0)}</strong>
                        </div>
                        {quoteError ? <p className="bk-field-err" style={{ margin: "8px 0 0" }}>{quoteError}</p> : null}
                      </div>
                    ) : null}
                    <div className="ev-search ev-search--checkout">
                      <input
                        type="search"
                        className="ev-search__input"
                        value={pkgQuery}
                        onChange={function (e) { setPkgQuery(e.target.value); }}
                        placeholder="Search events by name"
                        aria-label="Search events by name"
                      />
                      {pkgQuery ? (
                        <button type="button" className="ev-search__clear" onClick={function () { setPkgQuery(""); }}>Clear</button>
                      ) : null}
                    </div>
                    {selectedPinnedPackages.length === 0 && remainingVisiblePackages.length === 0 ? (
                      <div className="ev-search-empty">
                        <p>No events match “{pkgQuery.trim()}”.</p>
                        <button type="button" className="bk-btn bk-btn--ghost bk-btn--sm" onClick={function () { setPkgQuery(""); }}>Clear search</button>
                      </div>
                    ) : (
                    <>
                    {selectedPinnedPackages.length ? (
                      <div className="bk-staff-pkg-group bk-staff-pkg-group--selected">
                        <h4 className="bk-staff-pkg-group__title">Selected</h4>
                        <div className="bk-pkg-grid bk-pkg-grid--competition">
                          {selectedPinnedPackages.map(renderCompCard)}
                        </div>
                      </div>
                    ) : null}
                    {remainingVisiblePackages.length ? (
                      <div className="bk-pkg-grid bk-pkg-grid--competition">
                        {remainingVisiblePackages.map(renderCompCard)}
                      </div>
                    ) : pkgQuery.trim() && selectedPinnedPackages.length ? (
                      <div className="ev-search-empty">
                        <p>No other events match “{pkgQuery.trim()}”.</p>
                        <button type="button" className="bk-btn bk-btn--ghost bk-btn--sm" onClick={function () { setPkgQuery(""); }}>Clear search</button>
                      </div>
                    ) : null}
                    </>
                    )}
                    <CommitteeStatsBlock
                      groups={packagesByCommittee}
                      eventId={eventId}
                      gstLabel={gstLabel}
                      onSelectPackage={function (code) { toggleCart(code); scrollToCart(); }}
                    />
                  </>
                ) : (
                <div className="bk-pkg-grid">
                  {visiblePackages.map(function (pkg) {
                    var active = form.package_code === pkg.package_code;
                    var vtLabel = pkg.vehicle_type === "2W" ? "2W" : "4W";
                    var displayName = pkg.name.replace(/\s*\(4 Wheeler\)/i, "").replace(/\s*\(2 Wheeler\)/i, "");
                    var hideVehicleBadge = isCompetitionPkg(pkg);
                    return (
                      <button
                        key={pkg.package_code}
                        type="button"
                        className={"bk-pkg-card" + (active ? " bk-pkg-card--active" : "") + (fieldErrors.package_code ? " bk-pkg-card--err" : "")}
                        onClick={function () { field("package_code", pkg.package_code); }}
                      >
                        {hideVehicleBadge ? null : <span className={"bk-pkg-card__vtype bk-pkg-card__vtype--" + pkg.vehicle_type}>{vtLabel}</span>}
                        <span className="bk-pkg-card__price">&#8377;{parseFloat(pkg.price).toFixed(0)}<span className="ev-gst-note">{gstLabel}</span></span>
                        <span className="bk-pkg-card__name">{displayName}</span>
                        <span className="bk-pkg-card__inc-list">
                          {(pkg.included_services && pkg.included_services.length
                            ? pkg.included_services
                            : (isCompetitionPkg(pkg) ? ["Entry"] : ["Parking"])
                          ).map(function (svc) {
                            var isExtra = svc !== "Parking" && svc !== "Entry";
                            var chipLabel = svc.replace(/\s*\(\d+ Pax\)/i, "");
                            return <span key={svc} className={"bk-pkg-card__inc-chip" + (isExtra ? " bk-pkg-card__inc-chip--extra" : "")}>{isExtra ? "\u2713 " : ""}{chipLabel}</span>;
                          })}
                        </span>
                        {active && <span className="bk-pkg-card__check">&#10003;</span>}
                      </button>
                    );
                  })}
                </div>
                )}
                {fieldErrors.package_code && <p className="bk-field-err">{fieldErrors.package_code}</p>}

                {addons.length > 0 && !isCompetitionEvent && (
                  <div className="bk-addons">
                    <h4 className="bk-addons__title">Value Added Services</h4>
                    <div className="bk-addons__grid">
                    {(() => {
                      const SHARED_GROUP = ["SHARED_CART_ONE_WAY", "SHARED_CART_ROUND_TRIP"];
                      const PERSONAL_GROUP = ["PERSONAL_CART_ONE_WAY", "PERSONAL_CART_ROUND_TRIP"];
                      const CART_ALL = [...SHARED_GROUP, ...PERSONAL_GROUP];
                      return addons.map(svc => {
                        const selected = form.addons.find(a => a.service_code === svc.service_code);
                        const checked = !!selected;
                        const isPerPerson = svc.pricing_mode === "PER_PERSON";
                        const qty = selected ? selected.quantity : 1;
                        return (
                          <div key={svc.service_code} className={`bk-addon-row ${checked ? "bk-addon-row--on" : ""}${lockedAddonTier ? " bk-addon-row--locked" : ""}`} onClick={() => {
                            if (lockedAddonTier) return;
                            if (checked && isPerPerson) return;
                            field("addons", prev => {
                              const idx = prev.findIndex(a => a.service_code === svc.service_code);
                              if (idx >= 0) return prev.filter((_, i) => i !== idx);
                              let next = [...prev, { service_code: svc.service_code, quantity: 1 }];
                              if (CART_ALL.includes(svc.service_code)) {
                                const myGroup = SHARED_GROUP.includes(svc.service_code) ? SHARED_GROUP : PERSONAL_GROUP;
                                next = next.filter(a => {
                                  if (a.service_code === svc.service_code) return true;
                                  if (CART_ALL.includes(a.service_code)) return myGroup.includes(a.service_code) ? false : false;
                                  return true;
                                });
                                next = next.filter(a => !CART_ALL.includes(a.service_code) || a.service_code === svc.service_code);
                              }
                              return next;
                            });
                          }}>
                            <input type="checkbox" checked={checked} readOnly />
                            <span className="bk-addon-row__name">{svc.name}</span>
                            <span className="bk-addon-row__price" style={lockedAddonTier ? { textDecoration: "line-through", color: "#94a3b8" } : {}}>
                              &#8377;{parseFloat(svc.unit_price).toFixed(0)}
                              {isPerPerson && <span className="bk-addon-row__price-unit">/person</span>}
                              <span className="ev-gst-note">{gstLabel}</span>
                            </span>
                            {lockedAddonTier && (
                              <span className="bk-included-price__free" style={{ fontSize: "0.78rem", marginLeft: 4 }}>Included</span>
                            )}
                            {checked && isPerPerson && (
                              <div className="bk-addon-row__qty" style={lockedAddonTier ? { textDecoration: "line-through", opacity: 0.5 } : {}} onClick={e => e.stopPropagation()}>
                                <span className="bk-addon-row__qty-label">Persons</span>
                                <div className="bk-qty-stepper">
                                  <button type="button" className="bk-qty-stepper__btn" disabled={qty <= 1 || lockedAddonTier} onClick={() => {
                                    if (lockedAddonTier) return;
                                    field("addons", prev => prev.map(a => a.service_code === svc.service_code ? { ...a, quantity: Math.max(1, a.quantity - 1) } : a));
                                  }}>&minus;</button>
                                  <span className="bk-qty-stepper__val">{qty}</span>
                                  <button type="button" className="bk-qty-stepper__btn" disabled={qty >= 6 || lockedAddonTier} onClick={() => {
                                    if (lockedAddonTier) return;
                                    field("addons", prev => prev.map(a => a.service_code === svc.service_code ? { ...a, quantity: Math.min(6, a.quantity + 1) } : a));
                                  }}>+</button>
                                </div>
                                <span className="bk-addon-row__qty-total">&#8377;{(svc.unit_price * qty).toFixed(0)}</span>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                    </div>
                  </div>
                )}

                <div className="bk-actions bk-actions--sticky">
                  <button className="bk-btn bk-btn--primary bk-btn--lg" onClick={handleProceedStep1} disabled={busy === "quote" || (!!quoteError && !quote)}>
                    {busy === "quote" ? "Loading..." : "Continue to Details"}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="bk-card bk-card--step">
                <div className="bk-card__head">
                  <span className="bk-card__step-num">2</span>
                  <div>
                    <h2 className="bk-card__title">Your Details</h2>
                    <p className="bk-card__sub">{isCompetitionEvent
                      ? "Contact and college details for your entry pass"
                      : "Contact and vehicle information for your booking"}</p>
                  </div>
                </div>

                <fieldset className="bk-fieldset">
                  <legend className="bk-fieldset__legend">Contact Information</legend>
                  <div className="bk-form-row bk-form-row--2">
                    <div className="bk-field">
                      <label className="bk-label">Full Name <span className="bk-req">*</span></label>
                      <input className={`bk-input ${fieldErrors.name ? "bk-input--err" : ""}`} placeholder="John Doe" value={form.name} onChange={e => field("name", e.target.value)} />
                      {fieldErrors.name && <span className="bk-field-err">{fieldErrors.name}</span>}
                    </div>
                    <div className="bk-field">
                      <label className="bk-label">Email <span className="bk-req">*</span></label>
                      <input className={`bk-input ${fieldErrors.email ? "bk-input--err" : ""}`} type="email" placeholder="john@example.com" value={form.email} onChange={e => field("email", e.target.value)} />
                      {fieldErrors.email && <span className="bk-field-err">{fieldErrors.email}</span>}
                    </div>
                  </div>
                  <div className={"bk-form-row" + (isCompetitionEvent ? " bk-form-row--2" : " bk-form-row--1")}>
                    <div className="bk-field">
                      <label className="bk-label">Phone <span className="bk-req">*</span></label>
                      <div className="bk-input-prefix-wrap">
                        <span className="bk-input-prefix">+91</span>
                        <input className={`bk-input bk-input--prefixed ${fieldErrors.phone ? "bk-input--err" : ""}`} inputMode="numeric" maxLength={10} placeholder="9876543210" value={form.phone} onChange={e => field("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                      </div>
                      {fieldErrors.phone && <span className="bk-field-err">{fieldErrors.phone}</span>}
                    </div>
                    {isCompetitionEvent ? (
                      <CommitteeSelect
                        value={form.registration_committee}
                        options={committeeOptions}
                        error={fieldErrors.registration_committee}
                        locked={!!lockedCommittee}
                        hint={
                          lockedCommittee
                            ? "Set by your committee's registration link. It does not limit which games you can buy."
                            : "PR field only — it does not limit which games you can buy."
                        }
                        onChange={function (e) { field("registration_committee", e.target.value); }}
                      />
                    ) : null}
                  </div>
                  {isCompetitionEvent ? (
                    <div className="bk-form-row" style={{ display: "grid", gap: 12, marginTop: 12 }}>
                      <div className="bk-field">
                        <label className="bk-label">College Name <span className="bk-req">*</span></label>
                        <input className={`bk-input ${fieldErrors.college_name ? "bk-input--err" : ""}`} placeholder="Your college / institute" value={form.college_name} onChange={e => field("college_name", e.target.value)} />
                        {fieldErrors.college_name && <span className="bk-field-err">{fieldErrors.college_name}</span>}
                      </div>
                      <div className="bk-form-row bk-form-row--2">
                        <div className="bk-field">
                          <label className="bk-label">Year of Study <span className="bk-muted">(optional)</span></label>
                          <input className="bk-input" placeholder="e.g. 2nd Year" value={form.year_of_study} onChange={e => field("year_of_study", e.target.value)} />
                        </div>
                        <div className="bk-field">
                          <label className="bk-label">Course <span className="bk-muted">(optional)</span></label>
                          <input className="bk-input" placeholder="e.g. B.E. Computer" value={form.course} onChange={e => field("course", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </fieldset>


                {!requiresVehicle ? null : (
                <fieldset className="bk-fieldset">
                  <legend className="bk-fieldset__legend">Vehicle Information</legend>
                  <div className="bk-form-row bk-form-row--2">
                    <div className="bk-field">
                      <label className="bk-label">Vehicle Type</label>
                      <div className="bk-vtype-display">
                        <span className={"bk-vtype-badge bk-vtype-badge--" + form.vehicle_type}>
                          {form.vehicle_type === "2W" ? "2 Wheeler" : "4 Wheeler"}
                        </span>
                        <span className="bk-vtype-hint">Based on selected package</span>
                      </div>
                    </div>
                    <div className="bk-field">
                      <label className="bk-label">Vehicle Number <span className="bk-req">*</span></label>
                      <input className={"bk-input" + (fieldErrors.vehicle_number ? " bk-input--err" : "")} placeholder="DL01AB1234" maxLength={12} value={form.vehicle_number} onChange={function (e) { field("vehicle_number", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)); }} />
                      {fieldErrors.vehicle_number && <span className="bk-field-err">{fieldErrors.vehicle_number}</span>}
                    </div>
                  </div>
                  <div className="bk-form-row bk-form-row--2">
                    <div className="bk-field">
                      <label className="bk-label">Brand <span className="bk-opt">(optional)</span></label>
                      <input className="bk-input" placeholder="Maruti, Honda..." value={form.vehicle_brand} onChange={function (e) { field("vehicle_brand", e.target.value); }} />
                    </div>
                    <div className="bk-field">
                      <label className="bk-label">Color <span className="bk-opt">(optional)</span></label>
                      <input className="bk-input" placeholder="White, Black..." value={form.vehicle_color} onChange={function (e) { field("vehicle_color", e.target.value); }} />
                    </div>
                  </div>
                </fieldset>
                )}

                {/* Voice / IVR support-callback consent (MSG91 compliance).
                 *  Unchecked by default.  Booking is NOT blocked if the
                 *  user declines — both decisions are recorded. */}
                <fieldset className="bk-fieldset">
                  <legend className="bk-fieldset__legend">Support Callback (Optional)</legend>
                  <label
                    className={
                      "ev-terms-booking__check" +
                      (voiceConsent ? " ev-terms-booking__check--done" : "")
                    }
                    style={{ alignItems: "flex-start" }}
                  >
                    <input
                      type="checkbox"
                      checked={voiceConsent}
                      onChange={(e) => setVoiceConsent(e.target.checked)}
                    />
                    <span className="ev-terms-booking__check-label">
                      I agree to receive transactional voice / IVR support
                      callbacks from ParkAdda (and its authorised voice
                      provider) on +91 {form.phone || "XXXXXXXXXX"} for booking
                      assistance, on-ground support, and refund/settlement
                      queries. No marketing calls. Valid for up to 6 months;
                      I may opt out anytime from my booking details page or
                      by writing to support@parkadda.com. See&nbsp;
                      <Link
                        to="/events/terms-and-conditions#s16"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ev-terms-booking__text-btn ev-terms-booking__text-btn--inline"
                      >
                        Voice &amp; IVR Support terms
                      </Link>
                      .
                    </span>
                  </label>
                  <p className="bk-muted" style={{ marginTop: 6, fontSize: 12 }}>
                    Optional — leave unchecked if you do not want to receive
                    support callbacks. Your booking will proceed either way.
                  </p>
                </fieldset>

                <div className="bk-actions bk-actions--split bk-actions--sticky">
                  <button className="bk-btn bk-btn--ghost" onClick={() => goStep(1)}>Back</button>
                  <button className="bk-btn bk-btn--primary bk-btn--lg" onClick={handleProceedStep2} disabled={busy === "create"}>
                    {busy === "create" ? "Creating booking..." : "Continue to Verify"}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div className="bk-card bk-card--step">
                <div className="bk-card__head">
                  <span className="bk-card__step-num">3</span>
                  <div>
                    <h2 className="bk-card__title">Verify Phone</h2>
                    <p className="bk-card__sub">{otpSent
                      ? ("Enter the 4-digit code sent to +91 " + form.phone + ". It stays valid for 20 minutes.")
                      : ("We will send a 4-digit OTP to +91 " + form.phone + " to verify this booking. The code stays valid for 20 minutes.")}</p>
                  </div>
                </div>

                {!otpSent ? (
                  <div className="bk-otp-prompt">
                    <p>Click below to receive a one-time verification code on your registered mobile number. This ensures your booking is securely linked to your phone.</p>
                    <button className="bk-btn bk-btn--primary bk-btn--lg" onClick={handleSendOtp} disabled={busy === "otp"}>
                      {busy === "otp" ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                ) : !otpVerified ? (
                  <div className="bk-otp-verify">
                    <p className="bk-muted">Enter the 4-digit code sent to your phone. If it arrives late, keep this page open — the code is valid for 20 minutes. Use Resend if you still do not receive it.</p>
                    <div className="bk-otp-input-row">
                      <input
                        className={`bk-input bk-input--otp ${fieldErrors.otp ? "bk-input--err" : ""}`}
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="- - - -"
                        value={otp}
                        onChange={e => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 4)); setFieldErrors(p => ({ ...p, otp: "" })); clearMsg(); }}
                        onKeyDown={function (e) { if (e.key === "Enter" && otp.length >= 4) handleVerifyOtp(); }}
                        autoFocus
                      />
                      <button className="bk-btn bk-btn--primary" onClick={handleVerifyOtp} disabled={busy === "verify" || otp.length < 4}>
                        {busy === "verify" ? "Verifying..." : "Verify"}
                      </button>
                    </div>
                    {fieldErrors.otp && <p className="bk-field-err">{fieldErrors.otp}</p>}
                    <button className="bk-link-btn" onClick={handleSendOtp} disabled={busy === "otp"}>
                      {busy === "otp" ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>
                ) : (
                  <div className="bk-otp-done">
                    <div className="bk-otp-done__icon">&#10003;</div>
                    <p>Phone verified successfully.</p>
                  </div>
                )}

                <div className="bk-actions bk-actions--split bk-actions--sticky">
                  <button className="bk-btn bk-btn--ghost" onClick={() => goStep(2)}>Back</button>
                  {otpVerified && (
                    <button className="bk-btn bk-btn--primary bk-btn--lg" onClick={() => goStep(4)}>
                      Continue to Payment
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div className="bk-card bk-card--step">
                <div className="bk-card__head">
                  <span className="bk-card__step-num">4</span>
                  <div>
                    <h2 className="bk-card__title">Complete Payment</h2>
                    <p className="bk-card__sub">{isCompetitionEvent
                      ? "Review your entry summary and pay securely via Cashfree. Your QR entry pass is generated instantly after payment."
                      : "Review your booking summary below and proceed to pay securely via Cashfree payment gateway. Your QR parking pass will be generated instantly upon successful payment."}</p>
                  </div>
                </div>

                <div className="bk-pay-summary">
                  <div className="bk-pay-summary__row">
                    <span>Event</span>
                    <span>{event.title}</span>
                  </div>
                  <div className="bk-pay-summary__row">
                    <span>Name</span>
                    <span>{form.name}</span>
                  </div>
                  <div className="bk-pay-summary__row">
                    <span>Phone</span>
                    <span>{form.phone}</span>
                  </div>
                  {!isCompetitionEvent && (form.vehicle_number || "").trim() ? (
                  <div className="bk-pay-summary__row">
                    <span>Vehicle</span>
                    <span>{form.vehicle_type === "2W" ? "2 Wheeler" : "4 Wheeler"}{form.vehicle_number ? " \u00B7 " + form.vehicle_number : ""}</span>
                  </div>
                  ) : null}

                  <div className="bk-pay-summary__divider" />
                  <h5 className="bk-pay-summary__section-title">{isCompetitionEvent ? (cartLineItems.length > 1 ? "Games in this order" : "Competition Entry") : "Parking Package"}</h5>
                  {isCompetitionEvent && cartLineItems.length ? (
                    cartLineItems.map(function (line) {
                      return (
                        <div className="bk-pay-summary__row" key={line.package_code || line.package_name}>
                          <span>{line.package_name || line.name}</span>
                          <span>₹{parseFloat(line.price || line.total_amount || 0).toFixed(0)}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="bk-pay-summary__row">
                      <span>{selectedPkg ? selectedPkg.name : "\u2014"}</span>
                      <span>{selectedPkg ? "₹" + parseFloat(selectedPkg.price).toFixed(0) : "\u2014"}</span>
                    </div>
                  )}
                  {!isCompetitionEvent && selectedPkg && selectedPkg.included_services && selectedPkg.included_services.length > 0 && (
                    <div className="bk-pay-summary__row bk-pay-summary__row--muted">
                      <span>Includes: {selectedPkg.included_services.join(", ")}</span>
                    </div>
                  )}

                  {form.addons.length > 0 && <>
                    <div className="bk-pay-summary__divider" />
                    <h5 className="bk-pay-summary__section-title">Value Added Services</h5>
                    {form.addons.map(a => {
                      const svc = allAddons.find(s => s.service_code === a.service_code);
                      const isPerPerson = svc && svc.pricing_mode === "PER_PERSON";
                      const qty = a.quantity || 1;
                      const lineTotal = svc ? (isPerPerson ? svc.unit_price * qty : parseFloat(svc.unit_price)) : 0;
                      const included = lockedAddonTier;
                      return (
                        <div className="bk-pay-summary__row" key={a.service_code}>
                          <span>{svc ? svc.name : fmtService(a.service_code)}{isPerPerson && qty > 1 ? ` x${qty}` : ""}</span>
                          {included ? (
                            <span className="bk-included-price">
                              <span className="bk-included-price__strike">₹{lineTotal ? parseFloat(lineTotal).toFixed(0) : "—"}</span>
                              <span className="bk-included-price__free">Included</span>
                            </span>
                          ) : (
                            <span>₹{lineTotal ? parseFloat(lineTotal).toFixed(0) : "—"}</span>
                          )}
                        </div>
                      );
                    })}
                  </>}

                  {quote && (
                    <>
                      <div className="bk-pay-summary__divider" />
                      <h5 className="bk-pay-summary__section-title">Payment Summary</h5>
                      <GstPaymentSummary quote={quote} totalClass="bk-pay-summary__row--total" />
                    </>
                  )}
                </div>

                <EventTermsBookingBlock
                  eventId={eventId}
                  termsAccepted={termsAccepted}
                  onTermsChange={setTermsAcceptedSafe}
                  onOpenFull={() => setTermsModalOpen(true)}
                />
                <EventTermsModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />

                <div className="bk-actions bk-actions--sticky">
                  <button
                    className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                    onClick={handlePay}
                    disabled={busy === "pay" || !termsAccepted}
                  >
                    {busy === "pay" ? "Processing..." : ("Pay \u20B9" + ((quote && quote.total_amount) || "—"))}
                  </button>
                  <button className="bk-btn bk-btn--ghost" onClick={() => goStep(3)} style={{ marginTop: 8 }}>Back</button>
                </div>

              </div>
            )}

            {/* Step 5 — Booking Result */}
            {step === 5 && (
              <div className="bk-card bk-card--result">
                {!bookingResult && (
                  <div className="bk-loader-wrap" style={{ minHeight: "30vh" }}>
                    <div className="bk-spinner" />
                    <p>Checking payment status...</p>
                  </div>
                )}
                {bookingResult && bookingResult.booking_status === "CONFIRMED" && (
                  <>
                    <div className="bk-result-banner bk-result-banner--success">
                      <span className="bk-result-icon">&#10003;</span>
                      <h2>Booking Confirmed!</h2>
                      <p>{isCompetitionEvent ? "Your competition entry is confirmed." : "Your parking has been booked successfully."}</p>
                    </div>
                    {bookingResult.order && bookingResult.order.lines && bookingResult.order.lines.length > 1 ? (
                      <div>
                        {bookingResult.order.lines.map(function (line) {
                          return (
                            <div className="bk-result-qr" key={line.booking_id}>
                              {line.qr_code_url ? <img src={line.qr_code_url} alt={line.game_name + " QR"} className="bk-result-qr__img" /> : null}
                              <p className="bk-muted" style={{ fontWeight: 700 }}>{line.game_name}</p>
                              <p className="bk-muted">Scan only for this game · {line.booking_id}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : bookingResult.qr_code_url ? (
                      <div className="bk-result-qr">
                        <img src={bookingResult.qr_code_url} alt="QR Code" className="bk-result-qr__img" />
                        <p className="bk-muted">{isCompetitionEvent ? "Show this QR at the fest entry checkpoint" : "Show this QR at the parking entry checkpoint"}</p>
                      </div>
                    ) : null}
                    <div className="bk-result-details">
                      {bookingResult.order && bookingResult.order.order_id ? (
                        <div className="bk-result-row"><span>Order ID</span><span className="bk-mono">{bookingResult.order.order_id}</span></div>
                      ) : null}
                      <div className="bk-result-row"><span>Booking ID</span><span className="bk-mono">{bookingResult.booking_id}</span></div>
                      <div className="bk-result-row"><span>Name</span><span>{bookingResult.name}</span></div>
                      <div className="bk-result-row"><span>Phone</span><span>{bookingResult.phone}</span></div>
                      {bookingResult.college_name ? (
                        <div className="bk-result-row"><span>College</span><span>{bookingResult.college_name}</span></div>
                      ) : null}
                      {!isCompetitionEvent && (bookingResult.vehicle_number || "").trim() ? (
                      <div className="bk-result-row"><span>Vehicle</span><span>{bookingResult.vehicle_type === "2W" ? "2 Wheeler" : "4 Wheeler"} {"\u00B7"} {bookingResult.vehicle_number}</span></div>
                      ) : null}
                      <div className="bk-result-row"><span>{isCompetitionEvent ? ((bookingResult.order && bookingResult.order.lines && bookingResult.order.lines.length > 1) ? "Games" : "Competition") : "Package"}</span><span>{
                        (bookingResult.order && bookingResult.order.lines && bookingResult.order.lines.length)
                          ? bookingResult.order.lines.map(function (line) { return line.game_name; }).filter(Boolean).join(", ")
                          : (bookingResult.package ? bookingResult.package.name : "—")
                      }</span></div>
                      {bookingResult.registration_committee ? (
                        <div className="bk-result-row"><span>Committee</span><span>{formatCommitteeLabel(bookingResult.registration_committee)}</span></div>
                      ) : null}
                      {bookingResult.subtotal_amount || (bookingResult.order && bookingResult.order.subtotal_amount) ? (
                        <div className="bk-result-row"><span>Base amount</span><span>{"\u20B9"}{(bookingResult.order && bookingResult.order.subtotal_amount) || bookingResult.subtotal_amount}</span></div>
                      ) : null}
                      {bookingResult.gst_amount || (bookingResult.order && bookingResult.order.gst_amount) ? (
                        <div className="bk-result-row"><span>{bookingResult.gst_inclusive ? "GST included" : "GST"}</span><span>{"\u20B9"}{(bookingResult.order && bookingResult.order.gst_amount) || bookingResult.gst_amount}</span></div>
                      ) : null}
                      <div className="bk-result-row bk-result-row--total"><span>Total Paid</span><span>{"\u20B9"}{(bookingResult.order && bookingResult.order.total_amount) || bookingResult.total_amount}</span></div>
                    </div>
                    <EventTermsDisclaimerStrip className="ev-terms-disclaimer ev-terms-disclaimer--inline" />
                    <InvoiceWhatsAppActions
                      bookingId={bookingResult.booking_id}
                      paid={true}
                      phone={bookingResult.phone}
                      className="bk-actions"
                      primaryClass="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                      ghostClass="bk-btn bk-btn--ghost bk-btn--full"
                    />
                    <div className="bk-actions" style={{ marginTop: 12 }}>
                      <Link to={"/events/booking?booking_id=" + bookingResult.booking_id} className="bk-btn bk-btn--ghost bk-btn--full">View Full Booking Details</Link>
                      <Link to="/events" className="bk-btn bk-btn--ghost bk-btn--full" style={{ marginTop: 8 }}>Browse Events</Link>
                    </div>
                  </>
                )}
                {bookingResult && bookingResult.booking_status === "CANCELLED" && (
                  <>
                    <div className="bk-result-banner bk-result-banner--failed">
                      <span className="bk-result-icon bk-result-icon--fail">&#10007;</span>
                      <h2>Booking Cancelled</h2>
                      <p>This booking was cancelled. If you were charged, a refund has been initiated.</p>
                    </div>
                    <div className="bk-result-details">
                      <div className="bk-result-row"><span>Booking ID</span><span className="bk-mono">{bookingResult.booking_id}</span></div>
                      <div className="bk-result-row"><span>Status</span><span style={{ color: "#dc2626", fontWeight: 600 }}>CANCELLED</span></div>
                    </div>
                    <div className="bk-actions" style={{ marginTop: 20 }}>
                      <Link to={"/events/book?event_id=" + eventId} className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full">Book Again</Link>
                      <Link to="/events" className="bk-btn bk-btn--ghost bk-btn--full" style={{ marginTop: 8 }}>Browse Events</Link>
                    </div>
                  </>
                )}
                {bookingResult && (bookingResult.booking_status === "FAILED" || bookingResult.payment_status === "FAILED") && bookingResult.booking_status !== "CANCELLED" && (
                  <>
                    <div className="bk-result-banner bk-result-banner--failed">
                      <span className="bk-result-icon bk-result-icon--fail">&#10007;</span>
                      <h2>Payment Failed</h2>
                      <p>Your payment could not be processed. You can retry below.</p>
                    </div>
                    <div className="bk-result-details">
                      <div className="bk-result-row"><span>Booking ID</span><span className="bk-mono">{bookingResult.booking_id}</span></div>
                      <div className="bk-result-row"><span>Status</span><span style={{ color: "#dc2626", fontWeight: 600 }}>{bookingResult.payment_status}</span></div>
                    </div>
                    <div className="bk-actions" style={{ marginTop: 20 }}>
                      <button
                        className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                        onClick={handlePay}
                        disabled={busy === "pay" || !termsAccepted}
                      >
                        {busy === "pay" ? "Processing..." : "Retry Payment"}
                      </button>
                      <Link to="/events" className="bk-btn bk-btn--ghost bk-btn--full" style={{ marginTop: 8 }}>Browse Events</Link>
                    </div>
                  </>
                )}
                {bookingResult && bookingResult.booking_status !== "CONFIRMED" && bookingResult.booking_status !== "FAILED" && bookingResult.booking_status !== "CANCELLED" && bookingResult.payment_status !== "FAILED" && (
                  <div className="bk-loader-wrap" style={{ minHeight: "20vh" }}>
                    <div className="bk-spinner" />
                    <p>Payment is being processed... This will update automatically.</p>
                    <p className="bk-muted">Booking ID: {bookingResult.booking_id}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="bk-sidebar">
            <div className="bk-sidebar__card">
              <h4 className="bk-sidebar__title">Order Summary</h4>
              <div className="bk-sidebar__row"><span>Event</span><span className="bk-sidebar__val">{event.title}</span></div>
              {!isCompetitionEvent && (form.vehicle_number || "").trim() ? (
              <div className="bk-sidebar__row"><span>Vehicle</span><span className="bk-sidebar__val">{form.vehicle_type === "2W" ? "2 Wheeler" : "4 Wheeler"}{form.vehicle_number ? " \u00B7 " + form.vehicle_number : ""}</span></div>
              ) : null}
              <div className="bk-sidebar__divider" />
              <h5 style={{ margin: "0 0 6px", fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{isCompetitionEvent ? (cartLineItems.length > 1 ? "Games in this order" : "Competition Entry") : "Parking Package"}</h5>
              {isCompetitionEvent && cartLineItems.length ? (
                cartLineItems.map(function (line) {
                  return (
                    <div className="bk-sidebar__row" key={line.package_code || line.package_name}>
                      <span>{line.package_name || line.name}</span>
                      <span className="bk-sidebar__val">₹{parseFloat(line.price || line.total_amount || 0).toFixed(0)}</span>
                    </div>
                  );
                })
              ) : (
                <div className="bk-sidebar__row">
                  <span>{selectedPkg ? selectedPkg.name : "—"}{!isCompetitionEvent && selectedPkg && !(selectedPkg.included_services && selectedPkg.included_services.filter(s => s !== "Parking").length > 0) ? " (Parking)" : ""}</span>
                  <span className="bk-sidebar__val">{selectedPkg ? "₹" + parseFloat(selectedPkg.price).toFixed(0) : "—"}</span>
                </div>
              )}
              {isCompetitionEvent && cartLineItems.length > 1 ? (
                <div className="bk-sidebar__row" style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  <span>{cartLineItems.length} games · each game gets its own QR</span>
                </div>
              ) : null}
              {!isCompetitionEvent && selectedPkg && selectedPkg.included_services && selectedPkg.included_services.filter(s => s !== "Parking").length > 0 && (
                <div className="bk-sidebar__row" style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  <span>Includes: {selectedPkg.included_services.join(", ")}</span>
                </div>
              )}
              {form.addons.length > 0 && <>
                <h5 style={{ margin: "12px 0 6px", fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>Value Added Services</h5>
                {form.addons.map(a => {
                  const svc = allAddons.find(s => s.service_code === a.service_code);
                  const isPerPerson = svc && svc.pricing_mode === "PER_PERSON";
                  const qty = a.quantity || 1;
                  const lineTotal = svc ? (isPerPerson ? svc.unit_price * qty : parseFloat(svc.unit_price)) : 0;
                  const included = lockedAddonTier;
                  return (
                    <div className="bk-sidebar__row" key={a.service_code}>
                      <span>{svc ? svc.name : fmtService(a.service_code)}{isPerPerson && qty > 1 ? ` x${qty}` : ""}</span>
                      {included ? (
                        <span className="bk-included-price">
                          <span className="bk-included-price__strike">₹{lineTotal ? parseFloat(lineTotal).toFixed(0) : "—"}</span>
                          <span className="bk-included-price__free">Included</span>
                        </span>
                      ) : (
                        <span className="bk-sidebar__val">₹{lineTotal ? parseFloat(lineTotal).toFixed(0) : "—"}</span>
                      )}
                    </div>
                  );
                })}
              </>}
              <div className="bk-sidebar__divider" />
              {quote ? (
                <>
                  <GstPaymentSummary quote={quote} rowClass="bk-sidebar__row" totalClass="bk-sidebar__row--total" />
                </>
              ) : (
                <p className="bk-muted">Pricing loads after package selection.</p>
              )}
              {step === 5 && bookingId && (
                <>
                  <div className="bk-sidebar__divider" />
                  <div className="bk-sidebar__row"><span>Booking</span><span className="bk-sidebar__val bk-mono">{bookingId}</span></div>
                  <div className="bk-sidebar__row"><span>Phone</span><span className="bk-sidebar__val">{otpVerified ? "Verified ✓" : "Pending"}</span></div>
                </>
              )}
              {step === 1 && (
                <div className="bk-sidebar__cta">
                  <button
                    type="button"
                    className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                    onClick={handleProceedStep1}
                    disabled={busy === "quote" || !(form.package_code || cartCodes.length)}
                  >
                    {busy === "quote" ? "Loading..." : "Continue to Details"}
                  </button>
                  {!(form.package_code || cartCodes.length) ? <p className="bk-sidebar__cta-hint">Add one or more games to continue</p> : null}
                </div>
              )}
              {step === 2 && (
                <div className="bk-sidebar__cta">
                  <button
                    type="button"
                    className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                    onClick={handleProceedStep2}
                    disabled={busy === "create"}
                  >
                    {busy === "create" ? "Creating booking..." : "Continue to Verify"}
                  </button>
                </div>
              )}
              {step === 3 && otpVerified && (
                <div className="bk-sidebar__cta">
                  <button
                    type="button"
                    className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                    onClick={function () { goStep(4); }}
                  >
                    Continue to Payment
                  </button>
                </div>
              )}
              {step === 4 && (
                <div className="bk-sidebar__cta">
                  <button
                    type="button"
                    className="bk-btn bk-btn--primary bk-btn--lg bk-btn--full"
                    onClick={handlePay}
                    disabled={busy === "pay" || !termsAccepted}
                  >
                    {busy === "pay" ? "Processing..." : ("Pay \u20B9" + ((quote && quote.total_amount) || "—"))}
                  </button>
                </div>
              )}
            </div>
          </aside>
        </div>

        {/* Footer links */}
        <div className="bk-footer-links">
          <Link to="/events" className="bk-link-btn">Browse all events</Link>
          <span className="bk-footer-links__sep">·</span>
          <Link to="/events/terms-and-conditions" className="bk-link-btn">
            Terms &amp; Conditions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventBookingPlaceholder;
